/**
 * @architecture-header 1
 * @layer module-ui
 * @domain settings-student
 * @responsibility Render the SET-F10 student settings pane from backend-authoritative
 *   profile/preferences hooks (reused unmodified from SET-F02/SET-F03) plus this card's own
 *   password-change hook, gated to organization-session (org-login) students only.
 * @state-owner TanStack Query
 * @allowed-imports same-module hooks/components; settings-account and settings-preferences
 *   public hooks; app session provider; shared responsive/translator model
 * @forbidden direct API client; hardcoded profile data; teacher-only concepts (email change,
 *   linked-provider management)
 * @public-api StudentSettingsPage
 * @side-effects invokes authorized same-module and settings-account/settings-preferences commands
 * @tests StudentSettingsSurface.test.tsx
 */

import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useProfile, useUpdateProfile } from '../../settings-account';
import { usePreferences, useUpdatePreferences } from '../../settings-preferences';
import { useResetOrganizationPassword } from '../api/settings-student.queries';
import { StudentSettingsSurface } from './StudentSettingsSurface';

function errorCodeOf(error: unknown): string | null {
  return error && typeof error === 'object' && 'code' in error ? String(error.code) : null;
}

export function StudentSettingsPage({ mobile }: { mobile: boolean }) {
  const t = useTranslator();
  const { session } = useWorkspaceSession();
  const profileQuery = useProfile();
  const preferencesQuery = usePreferences();
  const updateProfile = useUpdateProfile();
  const updatePreferences = useUpdatePreferences();
  const changePassword = useResetOrganizationPassword();

  // An organization-session (org-login) student authenticates entirely via the cookie+CSRF
  // flow and has no linked Supabase/OAuth provider at all -- projectOrganizationSession in
  // SessionProvider.tsx sets linkedProviders: [] for exactly this population, unlike every
  // other session type. This is a read of already-existing session shape, not a new field.
  const isOrgLoginStudent = (session?.linkedProviders.length ?? -1) === 0;

  if (profileQuery.isPending || preferencesQuery.isPending) {
    return <VispoLoading className="student-settings-pane__loading" label={t('正在載入設定')} />;
  }

  if (!profileQuery.data || !preferencesQuery.data) {
    return (
      <div className="student-settings-pane__error" role="alert">
        <span>{t('更新失敗，請重試')}</span>
        <button
          type="button"
          onClick={() => {
            void profileQuery.refetch();
            void preferencesQuery.refetch();
          }}
        >
          {t('重新整理')}
        </button>
      </div>
    );
  }

  const profile = profileQuery.data;

  return (
    <StudentSettingsSurface
      avatarUrl={profile.avatar_url}
      locale={preferencesQuery.data.locale}
      mobile={mobile}
      onClearAvatar={() =>
        updateProfile.mutate({ clearAvatar: true, expectedRevision: profile.revision })
      }
      onRetry={() => {
        updateProfile.reset();
        void profileQuery.refetch();
      }}
      onSaveName={(displayName) =>
        updateProfile.mutate({ displayName, expectedRevision: profile.revision })
      }
      onSelectLocale={(locale) => updatePreferences.mutate(locale)}
      password={
        isOrgLoginStudent
          ? {
              errorCode: errorCodeOf(changePassword.error),
              onChangePassword: (currentPassword, newPassword) =>
                changePassword.mutate({
                  current_password: currentPassword,
                  new_password: newPassword,
                }),
              onResetError: () => {
                if (changePassword.error || changePassword.isSuccess) changePassword.reset();
              },
              saving: changePassword.isPending,
              success: changePassword.isSuccess,
            }
          : null
      }
      preferencesErrorCode={errorCodeOf(updatePreferences.error)}
      preferencesSaving={updatePreferences.isPending}
      profile={profile}
      profileErrorCode={errorCodeOf(updateProfile.error)}
      profileSaving={updateProfile.isPending}
    />
  );
}
